/**
 * HOF-216 — the one gate every unlink trigger goes through.
 *
 * Three affordances reach it (the on-edge ``Verknüpfung lösen`` control, the
 * right-click context menu, and the pre-existing Backspace/Delete shortcut)
 * and they converge here BEFORE any network call, so a twin write on a paying
 * tenant's ``gutschrift_match_group`` cannot reach the wire without the
 * operator having seen which two lines it will separate. The keyboard path is
 * gated too, deliberately: an unconfirmed keystroke over a 24 px hit target is
 * the accident this issue exists to prevent, and the shortcut is undiscoverable
 * today so nobody depends on its current speed.
 *
 * Pure + exported so all of that is testable without mounting ReactFlow — the
 * same pattern ``handleConnect.ts`` uses for the link direction.
 */
import { runManualLinkWithToast } from './lineLinkErrorToast';
import { isManualLineEdge, normaliseEdgeForUnlink, type EdgeLike, type LineSideMap } from './lineLinkValidation';
import type { LineUnlinkTelemetryPayload, LineUnlinkTrigger } from './lineUnlinkTelemetry';
import { buildUnlinkConfirmationMessage, type LineDescriptorMap } from './unlinkConfirmation';

/** One armed unlink, held while the confirmation is on screen. */
export interface PendingUnlink {
  /** The React Flow edge id — so a re-render can tell whether it is still on the canvas. */
  edgeId: string;
  /** Exactly the payload today's Backspace path produces. */
  args: { delivery_line_id: string; settlement_line_id: string };
  groupId: string | null;
  trigger: LineUnlinkTrigger;
  /** Pre-built so the modal renders no logic. */
  message: string;
}

export interface EdgeUnlinkDeps {
  lineSideMap: LineSideMap;
  lineDescriptors: LineDescriptorMap;
  /** Absent in Storybook / standalone renders — the whole affordance is then inert (AC 7). */
  onManualUnlink?: (args: { delivery_line_id: string; settlement_line_id: string }) => Promise<unknown>;
  showToast: (message: string, tone: 'error') => void;
  openConfirm: (pending: PendingUnlink) => void;
  reportTelemetry?: (payload: LineUnlinkTelemetryPayload) => void;
  /**
   * Pairs whose write is in the air. Owned by the caller (a ``useRef``) so it
   * survives the memo rebuild that every ``groups`` refetch causes — a guard
   * that resets on refetch is not a guard.
   *
   * Confirming closes the modal but deliberately leaves the edge on the canvas
   * until the refetch (AC 4/5), so every trigger stays live over a still-running
   * POST. Without this a second confirmation reaches the backend as a
   * *historical no-op* — the pair is already gone, so no error — which still
   * writes an audit record, still touches manual-resolution metadata, and still
   * emits a second unlink event, inflating the exact rate AC 9 exists to
   * measure. Found by codex's second pass.
   */
  inFlight: Set<string>;
  /**
   * HOF-219 — does this edge carry a DOCUMENT-level unlink?
   *
   * Injected rather than imported so this module keeps knowing about exactly
   * one layer. Its only job is to keep ``inert_edge`` honest: that event means
   * "an edge nothing severs at ANY layer", and the GS↔LS document edge stopped
   * being one. Counting it would keep raising the demand signal for a feature
   * that now ships — the metric would read as unmet need forever.
   *
   * Optional: absent means "nothing is severable at the document layer", which
   * is the pre-HOF-219 behaviour byte for byte.
   */
  hasDocumentUnlink?: (edge: EdgeLike) => boolean;
}

export interface EdgeUnlinkHandlers {
  /** Arm the confirmation for one edge. Returns whether it opened. */
  requestUnlink: (edge: EdgeLike, trigger: LineUnlinkTrigger) => boolean;
  /** React Flow's delete veto — the Backspace/Delete path. Always returns ``false``. */
  handleBeforeDelete: (params: { nodes: unknown[]; edges: EdgeLike[] }) => Promise<boolean>;
  /** The operator said yes. Issues exactly one write. */
  confirmUnlink: (pending: PendingUnlink) => Promise<void>;
}

/** Stable key for one line pair. Never rendered — an internal guard key only. */
function pairKey(args: { delivery_line_id: string; settlement_line_id: string }): string {
  return `${args.delivery_line_id}|${args.settlement_line_id}`;
}

export function buildEdgeUnlinkHandlers(deps: EdgeUnlinkDeps): EdgeUnlinkHandlers {
  // Telemetry must never block, delay or fail the operator's action (AC 9).
  // ``postTelemetryBeacon`` already swallows transport errors, but the caller
  // supplies this function, so the contract is enforced here rather than
  // assumed of every future caller.
  const report = (payload: LineUnlinkTelemetryPayload): void => {
    try {
      deps.reportTelemetry?.(payload);
    } catch {
      /* silent — see above */
    }
  };

  const requestUnlink = (edge: EdgeLike, trigger: LineUnlinkTrigger): boolean => {
    if (!deps.onManualUnlink) return false;
    const target = normaliseEdgeForUnlink(edge, deps.lineSideMap);
    if (!target) return false;
    if (deps.inFlight.has(pairKey(target))) return false;
    deps.openConfirm({
      edgeId: edge.id ?? `${target.delivery_line_id}_${target.settlement_line_id}`,
      args: {
        delivery_line_id: target.delivery_line_id,
        settlement_line_id: target.settlement_line_id,
      },
      groupId: target.groupId,
      trigger,
      message: buildUnlinkConfirmationMessage({
        delivery: deps.lineDescriptors.get(target.delivery_line_id),
        settlement: deps.lineDescriptors.get(target.settlement_line_id),
      }),
    });
    return true;
  };

  const handleBeforeDelete = async (params: { nodes: unknown[]; edges: EdgeLike[] }): Promise<boolean> => {
    // ALWAYS veto the local removal. Two reasons, and both are load-bearing:
    // cancelling the confirmation must leave the edge on the canvas (AC 4),
    // and on confirm the edge is removed by the ``matchGroupKeys.all`` refetch
    // instead — the same path that updates the group's status in place (AC 5).
    if (!deps.onManualUnlink) return false;

    // ``getElementsToRemove`` sweeps a selected NODE's connected edges into the
    // same payload, so a card selected with Delete pressed would otherwise read
    // as an unlink request for every edge touching it. This canvas offers no
    // node deletion at all — and ``onNodesChange`` IS wired, so the removal
    // would actually apply to the local node state.
    if (params.nodes.length > 0) return false;

    const manual = params.edges.find((edge) => isManualLineEdge(edge));
    if (manual) {
      requestUnlink(manual, 'keyboard');
      return false;
    }
    // HOF-219 — a document edge IS severable now, but deliberately not by this
    // key: AC 14 keeps ``isManualLineEdge``'s scoping of Backspace/Delete to
    // line edges unchanged, and a document sever can split a group into two
    // tiles and move money between them. It is a confirmed mouse gesture (the
    // on-edge control or the context menu), not a keystroke. So the keyboard
    // does nothing here — and, crucially, does not report ``inert_edge``, which
    // would keep counting demand for a feature that now exists.
    const severableElsewhere = params.edges.some((edge) => deps.hasDocumentUnlink?.(edge));
    if (params.edges.length > 0 && !severableElsewhere) {
      // A delete gesture on a genuinely inert edge: an Abrechnung edge, a
      // Zahlungsavis edge, a stub. Nothing severs those at any layer, and until
      // this event nothing counted the asking either.
      report({ kind: 'inert_edge', trigger: 'keyboard', group_id: null });
    }
    return false;
  };

  const confirmUnlink = async (pending: PendingUnlink): Promise<void> => {
    const call = deps.onManualUnlink;
    if (!call) return;
    const key = pairKey(pending.args);
    // Idempotent per pair for as long as the write is in the air — see
    // ``inFlight`` above. Claimed BEFORE the await so two synchronous confirms
    // cannot both pass the check.
    if (deps.inFlight.has(key)) return;
    deps.inFlight.add(key);
    let landed = false;
    await runManualLinkWithToast({
      call: async () => {
        try {
          const result = await call(pending.args);
          // Only a response that says it REMOVED something counts. The endpoint
          // accepts a repeat unlink as a historical no-op and 200s
          // (``stale_unlink`` fires only when a side is still paired), and the
          // canvas keeps the edge on screen until its refetch — so that window
          // is reachable and used to emit a second event claiming a write that
          // never happened. ``!== false`` is deliberate: an ABSENT field is a
          // backend predating the honesty fix, not evidence of a no-op, and
          // reading it as false would blank the metric across a rolling deploy.
          // (Codex fix round, P2.)
          landed = (result as { applied?: unknown } | null | undefined)?.applied !== false;
        } finally {
          // Released on EVERY outcome, including a 409 — a guard that a
          // rejected write leaves set would wedge that pair for the rest of
          // the session, which is worse than the duplicate it prevents.
          deps.inFlight.delete(key);
        }
      },
      showToast: deps.showToast,
      // #1087: no optimistic-edge state on the unlink path — the edge is still
      // on the canvas (we vetoed its removal) and the refetch takes it away.
      clearOptimisticEdge: () => undefined,
    });
    // Only a landed write is an unlink. A ``stale_unlink`` 409 leaves
    // ``line_pairs`` untouched, and counting it would overstate the very rate
    // this event exists to measure.
    if (landed) {
      report({ kind: 'unlink', trigger: pending.trigger, group_id: pending.groupId });
    }
  };

  return { requestUnlink, handleBeforeDelete, confirmUnlink };
}
